import {
  AMBIGUOUS_WESTERN_YEARS,
  ERA_ALIASES,
  ERAS,
  KNOWN_CASE_SYMBOLS,
  MAX_WESTERN_YEAR,
  MIN_WESTERN_YEAR,
} from './constants';
import { parseJapaneseNumber } from './numerals';
import { sanitizePreservingLength, stripInnerSpaces } from './sanitize';
import type {
  EraChoice,
  EraName,
  ExtractedCaseNumber,
  NormalizedCaseNumber,
  UnresolvedCandidate,
  WarningCode,
} from './types';

export type NormalizeOutcome =
  | { ok: true; value: Omit<NormalizedCaseNumber, 'id' | 'occurrence_count'> }
  | { ok: false; value: UnresolvedCandidate };

function unresolved(
  fragment: string,
  code: UnresolvedCandidate['reason_code'],
  reason: string,
): NormalizeOutcome {
  return { ok: false, value: { original_fragment: fragment, reason_code: code, reason } };
}

/** 西暦 → 元号。境界年（1912/1926/1989/2019）は月日がないと確定できない。 */
export function westernToEra(
  year: number,
): { ok: true; era: EraName; eraYear: number } | { ok: false; ambiguous: boolean } {
  if (!Number.isInteger(year) || year < MIN_WESTERN_YEAR || year > MAX_WESTERN_YEAR) {
    return { ok: false, ambiguous: false };
  }
  if (AMBIGUOUS_WESTERN_YEARS.has(year)) {
    return { ok: false, ambiguous: true };
  }
  const order: EraName[] = ['令和', '平成', '昭和', '大正', '明治'];
  for (const era of order) {
    const def = ERAS[era];
    if (year >= def.startYear) {
      return { ok: true, era, eraYear: year - def.startYear + 1 };
    }
  }
  return { ok: false, ambiguous: false };
}

export function eraYearLabel(year: number): string {
  return year === 1 ? '元' : String(year);
}

export function formatCaseNumber(
  era: EraName,
  year: number,
  symbol: string,
  serial: number,
): string {
  return `${era}${eraYearLabel(year)}年（${symbol}）第${serial}号`;
}

/**
 * 分解済みの候補 1 件を、決定論的に標準表記へ変換する。
 * AI は最終文字列を組み立てない。組み立てるのは常にこの関数。
 */
export function normalizeCaseNumber(candidate: ExtractedCaseNumber): NormalizeOutcome {
  const fragment = candidate.original_fragment?.trim() ?? '';
  if (fragment.length === 0) {
    return unresolved(
      fragment,
      'invalid_structure',
      '事件番号として認識できる文字列がありません。',
    );
  }

  const warnings: WarningCode[] = [];

  // --- 事件符号 ---
  const symbolRaw = stripInnerSpaces(
    sanitizePreservingLength(candidate.symbol_raw ?? '').replace(/[()]/g, ''),
  );
  if (symbolRaw.length === 0) {
    return unresolved(fragment, 'missing_symbol', '事件符号が読み取れませんでした。');
  }
  // 事件符号は全角のかな・漢字のまま扱う（半角カナは全角へ寄せる）
  const symbol = symbolRaw.normalize('NFKC');
  if (!KNOWN_CASE_SYMBOLS.has(symbol)) {
    warnings.push('unknown_case_symbol');
  }

  // --- 通し番号 ---
  const serialRaw = stripInnerSpaces(
    sanitizePreservingLength(candidate.serial_raw ?? '')
      .replace(/^第/, '')
      .replace(/号$/, ''),
  );
  if (serialRaw.length === 0) {
    return unresolved(fragment, 'missing_serial_number', '通し番号が読み取れませんでした。');
  }
  const serial = parseJapaneseNumber(serialRaw);
  if (serial === null || serial <= 0) {
    return unresolved(
      fragment,
      'missing_serial_number',
      '通し番号を数値として解釈できませんでした。',
    );
  }

  // --- 年 ---
  const yearRawSan = stripInnerSpaces(sanitizePreservingLength(candidate.year_raw ?? '')).replace(
    /年$/,
    '',
  );
  if (yearRawSan.length === 0) {
    return unresolved(fragment, 'missing_year', '年が読み取れませんでした。');
  }
  const yearNumber = yearRawSan === '元' ? 1 : parseJapaneseNumber(yearRawSan);
  if (yearNumber === null || yearNumber <= 0) {
    return unresolved(fragment, 'missing_year', '年を数値として解釈できませんでした。');
  }

  // --- 元号 ---
  let era: EraName;
  let eraYear: number;

  if (candidate.calendar_type === 'western') {
    const converted = westernToEra(yearNumber);
    if (!converted.ok) {
      if (converted.ambiguous) {
        return ambiguousYearOutcome(fragment, yearNumber, symbol, serial);
      }
      return unresolved(fragment, 'invalid_structure', '西暦として解釈できない年です。');
    }
    era = converted.era;
    eraYear = converted.eraYear;
  } else {
    const eraKey = stripInnerSpaces(sanitizePreservingLength(candidate.era_raw ?? ''));
    const mapped = ERA_ALIASES[eraKey];
    if (!mapped) {
      // 元号が無いが 4 桁相当なら西暦として救済する
      if (yearNumber >= MIN_WESTERN_YEAR && yearNumber <= MAX_WESTERN_YEAR) {
        const converted = westernToEra(yearNumber);
        if (!converted.ok) {
          if (converted.ambiguous) {
            return ambiguousYearOutcome(fragment, yearNumber, symbol, serial);
          }
          return unresolved(fragment, 'invalid_structure', '西暦として解釈できない年です。');
        }
        era = converted.era;
        eraYear = converted.eraYear;
      } else {
        return unresolved(fragment, 'missing_era', '元号が特定できませんでした。');
      }
    } else {
      era = mapped;
      eraYear = yearNumber;
    }
  }

  const def = ERAS[era];
  if (def.maxYear !== null && eraYear > def.maxYear) {
    warnings.push('era_year_out_of_range');
  }

  return {
    ok: true,
    value: {
      original_fragment: fragment,
      normalized: formatCaseNumber(era, eraYear, symbol, serial),
      era,
      year: eraYear,
      symbol,
      serial_number: serial,
      context_inferred: Boolean(candidate.context_inferred),
      warnings,
      source_index: candidate.source_index,
    },
  };
}

/**
 * 改元年で選べる元号の候補を作る。
 * 例：2019 → [平成31年, 令和元年]（古い元号を先に並べる）
 */
export function boundaryEraChoices(year: number, symbol: string, serial: number): EraChoice[] {
  const order: EraName[] = ['明治', '大正', '昭和', '平成', '令和'];
  const newerIndex = order.findIndex((e) => ERAS[e].startYear === year);
  if (newerIndex <= 0) return [];
  const newer = order[newerIndex];
  const older = order[newerIndex - 1];
  const olderYear = year - ERAS[older].startYear + 1;
  return [
    {
      label: `${older}${eraYearLabel(olderYear)}年`,
      era: older,
      year: olderYear,
      normalized: formatCaseNumber(older, olderYear, symbol, serial),
    },
    {
      label: `${newer}元年`,
      era: newer,
      year: 1,
      normalized: formatCaseNumber(newer, 1, symbol, serial),
    },
  ];
}

/**
 * 改元年の未確定候補。事件番号としては認識できているので、
 * ユーザーが元号を選べる形で返す（周辺の日付から自動推定はしない）。
 */
function ambiguousYearOutcome(
  fragment: string,
  year: number,
  symbol: string,
  serial: number,
): NormalizeOutcome {
  return {
    ok: false,
    value: {
      original_fragment: fragment,
      reason_code: 'ambiguous_western_year',
      reason: `${year}年は改元年のため、元号を特定できません。該当する元号を選択してください。`,
      pending_era_selection: {
        display: `${year}年（${symbol}）第${serial}号`,
        western_year: year,
        symbol,
        serial_number: serial,
        occurrence_count: 1,
        choices: boundaryEraChoices(year, symbol, serial),
      },
    },
  };
}

/**
 * 候補列 → 重複除去済みの最終結果。
 * 重複判定は必ず変換後の `normalized` 文字列で行い、最初に出現した順序を保つ。
 */
export function normalizeAll(candidates: ExtractedCaseNumber[]): {
  items: NormalizedCaseNumber[];
  unresolved: UnresolvedCandidate[];
} {
  const byNormalized = new Map<string, NormalizedCaseNumber>();
  const unresolvedList: UnresolvedCandidate[] = [];
  const seenUnresolved = new Map<string, UnresolvedCandidate>();

  for (const candidate of candidates) {
    const outcome = normalizeCaseNumber(candidate);
    if (!outcome.ok) {
      // 改元年の候補は「同じ事件番号」として数えたいので、表示文字列で重複判定する
      const key = outcome.value.pending_era_selection
        ? `pending:${outcome.value.pending_era_selection.display}`
        : `${outcome.value.original_fragment} ${outcome.value.reason_code}`;
      const existingUnresolved = seenUnresolved.get(key);
      if (existingUnresolved) {
        if (existingUnresolved.pending_era_selection) {
          existingUnresolved.pending_era_selection.occurrence_count += 1;
        }
      } else {
        seenUnresolved.set(key, outcome.value);
        unresolvedList.push(outcome.value);
      }
      continue;
    }
    const existing = byNormalized.get(outcome.value.normalized);
    if (existing) {
      existing.occurrence_count += 1;
      // 文脈から補ったものより、明示的に書かれていた出現を優先して代表にする
      if (existing.context_inferred && !outcome.value.context_inferred) {
        existing.context_inferred = false;
        existing.original_fragment = outcome.value.original_fragment;
      }
      for (const w of outcome.value.warnings) {
        if (!existing.warnings.includes(w)) existing.warnings.push(w);
      }
      continue;
    }
    byNormalized.set(outcome.value.normalized, {
      ...outcome.value,
      id: '',
      occurrence_count: 1,
    });
  }

  const items = [...byNormalized.values()]
    .sort((a, b) => (a.source_index ?? 0) - (b.source_index ?? 0))
    .map((item, i) => ({ ...item, id: `case-${i + 1}` }));

  // 成功した事件番号と同じ断片が unresolved に残っていたら取り除く
  const resolvedFragments = new Set(items.map((i) => i.original_fragment));
  const filteredUnresolved = unresolvedList.filter(
    (u) => !resolvedFragments.has(u.original_fragment),
  );

  return { items, unresolved: filteredUnresolved };
}
